/**
 * RealPagePreviews — Home, Agents, Chat, Conversation, Library and Edit Agent,
 * rendered as the REAL pages rather than lookalikes of them.
 *
 * This follows the rule the login preview established: a preview built by
 * re-drawing a surface at small size always drifts, because nothing forces the
 * copy to follow the original. So every surface here does one of two things,
 * and never invents a class name:
 *
 *   • Mounts the real component. The `Sidebar` (every full page),
 *     `AgentGallery` (Home), `BotListItem` (Agents), `MessageList` and
 *     `ConversationHeaderTitle` (Conversation) and `BotModal` (Edit Agent) all
 *     render here exactly as they do in the app — including the tenant's own
 *     logo, because `PreviewScope` puts a `BrandScope` above them and
 *     `useBrand()` answers with the tenant being edited.
 *   • Mirrors the page's own wrapper markup class for class, where the wrapper
 *     is inline in a page component that cannot be mounted (it would fetch,
 *     redirect, or rewrite the URL). The classes are the page's; only the data
 *     is a fixture.
 *
 * Each surface is laid out at a real viewport size inside `PreviewViewport`
 * and then scaled, so every media query, flex ratio and type step behaves as
 * it does for a user.
 *
 * Two pages fight back, and both are handled in console-shell.css rather than
 * here, because the cause is a global stylesheet rule and not this component:
 * `ChatPage.css` sets `html:has(.chat-main-content){overflow:hidden!important}`
 * (which would freeze the editor's own scrolling), and `.modal-overlay` is
 * `position: fixed` (which would escape the scaled box). See `.wl-real-preview`.
 */

import { useMemo } from 'react';
import type { BrandConfig } from '@/brand/types';
import type { Bot, Message } from '@/types/models';
import { Sidebar } from '@/components/layout/Sidebar';
import { AgentGallery } from '@modules/dashboard/components/agentGallery/AgentGallery';
import BotListItem from '@modules/chatbots/components/BotListItem';
import { MessageList } from '@modules/conversations/components/MessageList';
import ConversationHeaderTitle from '@modules/conversations/components/ConversationHeaderTitle';
import { BotModal } from '@modules/chatbots/components/BotModal';
import { PreviewViewport } from './PreviewSurfaces';
import { useLanguage } from '@/hooks/useLanguage';
import type { TFunc } from '@/utils/tfunc';

/* ─── Fixtures ─────────────────────────────────────────────────────────
   Shaped like real rows, not like the markup. A fixture that mirrors the
   API's shape keeps the preview honest: if a field the page relies on is
   renamed, the preview breaks in the same place the page does. */

const NOW = '2026-09-11T09:00:00Z';

function makeBot(over: Partial<Bot> & { chatbot_id: string; chatbot_name: string }): Bot {
  return {
    chatbot_description: '',
    chatbot_visibility: 'team',
    is_private: false,
    created_at: NOW,
    updated_at: NOW,
    created_by: 'preview',
    created_by_user_id: 'preview',
    tenant_id: 'preview',
    is_owner: true,
    ...over,
  } as Bot;
}

// Sample content is CONTENT: a Hebrew admin previewing their brand has to see
// the type set in Hebrew, or the preview cannot answer the one question it
// exists to answer. So these read through `t()` like everything else.
const sampleBots = (t: TFunc): Bot[] => ([
  makeBot({
    chatbot_id: 'p1',
    chatbot_name: t('designSystem.preview.bots.research.name'),
    chatbot_description: t('designSystem.preview.bots.research.desc'),
    is_favorited: true,
  }),
  makeBot({
    chatbot_id: 'p2',
    chatbot_name: t('designSystem.preview.bots.policy.name'),
    chatbot_description: t('designSystem.preview.bots.policy.desc'),
  }),
  makeBot({
    chatbot_id: 'p3',
    chatbot_name: t('designSystem.preview.bots.data.name'),
    chatbot_description: t('designSystem.preview.bots.data.desc'),
  }),
  makeBot({
    chatbot_id: 'p4',
    chatbot_name: t('designSystem.preview.bots.onboarding.name'),
    chatbot_description: t('designSystem.preview.bots.onboarding.desc'),
  }),
]);

const sampleMessages = (t: TFunc): Message[] => ([
  {
    message_id: 'm1',
    conversation_id: 'c1',
    role: 'user',
    content: t('designSystem.preview.msg.userQuarter'),
    created_at: NOW,
  },
  {
    message_id: 'm2',
    conversation_id: 'c1',
    role: 'assistant',
    content: t('designSystem.preview.msg.assistantQuarter'),
    created_at: NOW,
  },
  {
    message_id: 'm3',
    conversation_id: 'c1',
    role: 'user',
    content: t('designSystem.preview.msg.userSupport'),
    created_at: NOW,
  },
]);

/** AgentGallery filters by Set membership, so pass Sets — and hoist them so
 *  a new identity on every render cannot invalidate its memos. */
const FAVOURITE_IDS = new Set(['p1']);
const RECENT_IDS = new Set(['p2']);

const greetingName = (t: TFunc) => t('designSystem.preview.greetingName');

/**
 * The real navigation rail, in preview mode.
 *
 * This is where a tenant's logo actually appears on every page after sign-in,
 * so a preview without it cannot answer "what will my people see?". It renders
 * the app's own Sidebar — same markup, same nav, same brand mark — with its
 * fetches suppressed, and `PreviewScope`'s BrandScope makes `useBrandAssets()`
 * inside it resolve the tenant being edited rather than the signed-in admin.
 */
function PreviewSidebar({ activeItem }: { activeItem: string }) {
  return (
    <Sidebar
      options={{
        preview: true,
        activeItem,
        showHome: true,
        showChat: true,
        showBots: true,
        showSearch: true,
        showConversations: false,
        showBotsList: false,
      }}
    />
  );
}

/** The greeting exactly as MainPage builds it: a tenant's own
 *  `terminology.greeting` (with its `{{name}}` placeholder) wins over the
 *  i18n default, which is what makes this row worth previewing at all. */
function previewGreeting(merged: BrandConfig, t: TFunc): string {
  const name = greetingName(t);
  const custom = merged.terminology?.greeting;
  if (custom) return custom.replace(/\{\{\s*name\s*\}\}/g, () => name);
  return t('dashboard.welcome', { name });
}

/* ─── Home ─────────────────────────────────────────────────────────────
   `MainPage` itself cannot be mounted: it fetches its agents before it
   renders any, plays a fixed-position splash overlay that would escape the
   scaled box, writes sessionStorage, and navigates away when a conversation
   query param is present. So its wrapper markup is mirrored and the part that
   actually carries the brand — the agent gallery — is the real component. */
export function HomePreview({ merged }: { merged: BrandConfig }) {
  const { t } = useLanguage();
  const semi = merged.homeSemicircle?.enabled;
  return (
    <PreviewViewport width={1440} height={900}>
      <div className="home-layout phase-pick entry-done wl-real-preview">
        <PreviewSidebar activeItem="home" />
        <main className="home-main" data-agent-color="blue" data-home-semi={semi ? 'true' : undefined}>
          {semi && <div className="welcome-semi" />}

          <div className="welcome-greeting">
            <h1 className="welcome-title">
              <span className="welcome-title-line">{previewGreeting(merged, t)}</span>
              <span className="welcome-title-line welcome-title-question">
                {merged.terminology?.greetingQuestion || t('dashboard.welcomeQuestion')}
              </span>
            </h1>
          </div>

          <div className="agents-section">
            <AgentGallery
              bots={sampleBots(t)}
              favoriteIds={FAVOURITE_IDS}
              recentIds={RECENT_IDS}
              onOpenAgent={() => {}}
              onHoverAgent={() => {}}
              onToggleFavorite={() => {}}
            />
          </div>
        </main>
      </div>
    </PreviewViewport>
  );
}

/* ─── Agents ───────────────────────────────────────────────────────────
   The rows are the real `BotListItem`. Everything around them is the page's
   own markup: `BotsPage` would otherwise spend its first render on a spinner
   while `botService.getBots()` is in flight. */
export function AgentsPreview() {
  // These previews mirror real pages, so they must read the SAME
  // translation keys those pages read. Hardcoding the English here
  // made the tool that exists to check a Hebrew tenant's branding
  // render English chrome (branch review, 2026-09-11).
  const { t } = useLanguage();
  const bots = sampleBots(t);
  const favourites = bots.filter(b => b.is_favorited);
  const rest = bots.filter(b => !b.is_favorited);
  // The page's heading is a translated string, not a brand knob — BrandConfig
  // carries no per-noun terminology. Hardcoding the English here matches what
  // the page renders for an English user, which is what the preview claims.
  const agentsWord = t('agents.title');

  return (
    <PreviewViewport width={1440} height={900}>
      <div className="bots-layout wl-real-preview">
        <PreviewSidebar activeItem="bots" />
        <main className="bots-main">
          <div className="bots-content">
            <div className="agents-tabs">
              <button className="agents-tab-btn active" type="button">{agentsWord}</button>
              <button className="agents-tab-btn" type="button">{t('skills.title')}</button>
              <button className="agents-tab-btn" type="button">{t('dataBundles.title')}</button>
            </div>

            <div className="simple-chat-header">
              <h1 className="bot-title">{agentsWord}</h1>
              <p className="text-muted">{t('agents.subtitle')}</p>
            </div>

            <div className="bots-controls">
              <div className="bots-search-box">
                <svg className="bots-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input className="bots-search-input" placeholder={`${t('common.search')}…`} readOnly />
              </div>
              <select className="bots-filter-select" disabled>
                <option>{t('designSystem.preview.filterAll')}</option>
              </select>
              <button className="bots-create-btn" type="button">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                {t('designSystem.preview.newAgent')}
              </button>
            </div>

            <div className="bots-list-container">
              {favourites.length > 0 && (
                <section className="bots-section">
                  <div className="bots-section-header">
                    <h2 className="bots-section-title">
                      <span className="bots-section-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                          <path d="M12 2l3 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.9 21l1.2-6.8-5-4.9 6.9-1z" />
                        </svg>
                      </span>
                      Favorites
                    </h2>
                  </div>
                  <div className="bots-list">
                    {favourites.map(b => <BotListItem key={b.chatbot_id} bot={b} />)}
                  </div>
                </section>
              )}

              <section className="bots-section">
                <div className="bots-section-header">
                  <h2 className="bots-section-title">{t('agents.allAgents')}</h2>
                </div>
                <div className="bots-list">
                  {rest.map(b => <BotListItem key={b.chatbot_id} bot={b} />)}
                </div>
              </section>
            </div>
          </div>
        </main>
      </div>
    </PreviewViewport>
  );
}

/* ─── Conversation ─────────────────────────────────────────────────────
   The thread: the real header title, the real `MessageList` (so bubbles,
   avatars, markdown and action rows are the page's own), and the input row
   mirrored. `ChatInput` is not mounted because it fetches the model catalog
   on mount; `ConversationPanel` is not mounted because it renders only a
   spinner until its init effect resolves and calls `history.replaceState`,
   which would rewrite the editor's URL. */
function ConversationBody({ showHeader = true }: { showHeader?: boolean }) {
  const { t } = useLanguage();
  return (
    <div className="conversation-panel" data-panel="preview" data-agent-color="blue" data-tint-by-agent="true">
      {showHeader && (
        <div className="simple-chat-header">
          <div className="chat-header-title-container">
            <div className="chat-header-bot-info">
              <ConversationHeaderTitle
                displayName={t('designSystem.preview.bots.research.name')}
                fallbackTitle={t('designSystem.preview.generalChat')}
              />
            </div>
          </div>
        </div>
      )}

      <div className="chat-messages-container">
        <MessageList messages={sampleMessages(t)} showSkillLabels={false} />
      </div>

      <div className="chat-input-container">
        <div className="chat-input-wrapper">
          <div className="chat-input-group">
            <div className="chat-input-row-text chat-input-mirror-host">
              <textarea
                className="chat-input ltr-input"
                placeholder={t('chat.inputPlaceholder')}
                readOnly
                rows={1}
              />
            </div>
            <div className="chat-input-row-tools">
              <div className="input-tools-left">
                <button className="attach-btn" type="button" aria-label={t('chat.attach')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                </button>
              </div>
              <div className="input-send-area">
                <button className="send-button" type="button" aria-label={t('chat.send')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ConversationPreview() {
  return (
    <PreviewViewport width={1440} height={900}>
      <div className="wl-real-preview" style={{ height: '100%' }}>
        <ConversationBody />
      </div>
    </PreviewViewport>
  );
}

/* ─── Chat ─────────────────────────────────────────────────────────────
   The chat page is the same conversation inside the branch rail — that rail
   is what makes it a different surface, so the preview shows it rather than
   repeating the conversation preview. */
export function ChatPreview() {
  return (
    <PreviewViewport width={1440} height={900}>
      <div className="chat-layout wl-real-preview">
        <PreviewSidebar activeItem="chat" />
        <main className="chat-main-content" data-agent-color="blue" data-tint-by-agent="true">
          <div className="branch-rail">
            <div
              className="branch-rail-track"
              style={{ '--branch-count': 1, '--active-idx': 0 } as React.CSSProperties}
            >
              <div className="branch-slide is-active" data-active="true">
                <ConversationBody />
              </div>
            </div>
          </div>
        </main>
      </div>
    </PreviewViewport>
  );
}

/* ─── Library ──────────────────────────────────────────────────────────
   `VaultPage` has no extractable presentational piece — every cell and tree
   renderer is a local closure inside a 3,000-line component, and both its
   panels render an empty state until several fetches land. Its wrapper markup
   is mirrored class for class so the brand's surfaces, borders and type are
   exercised on the real selectors. */
const libraryFiles = (t: TFunc) => ([
  { name: t('designSystem.preview.files.boardPack'), kind: 'pdf', owner: t('designSystem.preview.owners.dana'), when: t('designSystem.preview.when.today') },
  { name: t('designSystem.preview.files.checklist'), kind: 'doc', owner: t('designSystem.preview.owners.dana'), when: t('designSystem.preview.when.yesterday') },
  { name: t('designSystem.preview.files.pricing'),   kind: 'xls', owner: t('designSystem.preview.owners.noam'), when: t('designSystem.preview.when.threeDays') },
  { name: t('designSystem.preview.files.brand'),     kind: 'pdf', owner: t('designSystem.preview.owners.noam'), when: t('designSystem.preview.when.lastWeek') },
]);

export function LibraryPreview({ merged }: { merged: BrandConfig }) {
  const { t } = useLanguage();
  const libraryWord = t('vault.title');
  return (
    <PreviewViewport width={1440} height={900}>
      <div className="admin-container wl-real-preview">
        <PreviewSidebar activeItem="admin" />
        <div className="main-container">
          <main className="main-content">
            <div className="vault-page">
              <div className="vault-header">
                <div className="vault-header-title">
                  <div className="vault-header-icon">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h1>{libraryWord}</h1>
                    <p className="vault-header-sub">{t('vault.subtitle')}</p>
                  </div>
                </div>
                <div className="vault-header-actions">
                  <button className="vault-map-btn" type="button">{t('vault.accessMapButton')}</button>
                </div>
              </div>

              <div className="vault-body">
                {/* The real page sets this width from a resizable state value;
                    the preview pins it in CSS (.wl-real-preview .vault-nav) so
                    no raw pixel literal lives in the markup. */}
                <div className="vault-panel vault-nav">
                  <div className="vault-new-wrap">
                    <button className="vault-new-btn" type="button">
                      <span className="vault-new-plus">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                      </span>
                      {t('vault.new')}
                    </button>
                  </div>
                  <div className="vault-nav-head">{t('vault.navigator')}</div>
                  <div className="vault-nav-tree">
                    <div className="vault-nav-section">{t('vault.section.org')}</div>
                    <span className="vault-tree-node vault-org-root">
                      <span className="vault-tree-text">
                        <span className="vault-tree-label" dir="auto">{merged.name || 'Organization'}</span>
                      </span>
                    </span>
                    {['Policies', 'Marketing', 'Finance'].map(n => (
                      <span className="vault-tree-node" key={n}>
                        <span className="vault-tree-text">
                          <span className="vault-tree-label">{n}</span>
                        </span>
                      </span>
                    ))}
                    <div className="vault-nav-section">{t('vault.section.shared')}</div>
                    <span className="vault-tree-node">
                      <span className="vault-tree-text">
                        <span className="vault-tree-label" dir="auto">{t('designSystem.preview.boardMaterials')}</span>
                      </span>
                      <span className="vault-shared-mark" />
                    </span>
                  </div>
                  <div className="vault-nav-blank" />
                </div>

                <div className="vault-resizer"><span className="vault-resizer-grip" /></div>

                <div className="vault-panel vault-main">
                  <div className="vault-main-head">
                    <div className="vault-breadcrumb">
                      <span className="vault-crumb-part">
                        <span className="crumb org" dir="auto">{merged.name || 'Organization'}</span>
                      </span>
                      <span className="vault-crumb-part">
                        <span className="crumb-sep">/</span>
                        <button className="crumb current" type="button">Policies</button>
                      </span>
                    </div>
                    <div className="vault-title-row">
                      <div className="vault-title-left">
                        <h2>Policies</h2>
                        <span className="vault-access-chip">{t('vault.accessEditor')}</span>
                      </div>
                      <div className="vault-toolbar">
                        <button className="vault-share-btn" type="button">{t('vault.share.share')}</button>
                      </div>
                    </div>
                  </div>

                  <table className="vault-preview-table">
                    <thead>
                      <tr>
                        <th>{t('vault.fileName')}</th>
                        <th>{t('vault.ownerCol')}</th>
                        <th>{t('vault.lastModified')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {libraryFiles(t).map(f => (
                        <tr key={f.name}>
                          <td>
                            <span className="vault-file-name">
                              <span className={`vault-file-icon ${f.kind}`}>
                                <span className="vault-file-glyph">{f.kind.toUpperCase()}</span>
                              </span>
                              <span className="vault-file-title" dir="auto">{f.name}</span>
                            </span>
                          </td>
                          <td>
                            <span className="vault-owner-cell">
                              <span className="vault-owner-coin">{f.owner.slice(0, 1)}</span>
                              <span className="vault-owner-name">{f.owner}</span>
                            </span>
                          </td>
                          <td><span className="vault-cell-muted">{f.when}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </PreviewViewport>
  );
}

/* ─── Edit Agent ───────────────────────────────────────────────────────
   The real `BotModal`, given a fixture agent and its `preview` flag. It seeds
   its form state from the `bot` prop, so the Details tab renders its actual
   markup on first paint; `preview` suppresses the reads it would otherwise
   make, which for a fixture id are requests for a row that does not exist. */
export function EditAgentPreview() {
  const { t } = useLanguage();
  const bot = useMemo(
    () =>
      makeBot({
        chatbot_id: 'preview-edit',
        chatbot_name: t('designSystem.preview.bots.research.name'),
        chatbot_description: t('designSystem.preview.bots.research.desc'),
        chatbot_system_prompt: t('designSystem.preview.editAgent.systemPrompt'),
      }),
    [t],
  );

  return (
    <PreviewViewport width={1440} height={900}>
      {/* `.modal-overlay` is position:fixed in the app. Inside a scaled box
          that would anchor it to the browser window instead of the preview, so
          `.wl-real-preview` re-scopes it to absolute. */}
      <div className="wl-real-preview wl-real-preview--modal">
        <BotModal preview isOpen bot={bot} onClose={() => {}} onSuccess={() => {}} />
      </div>
    </PreviewViewport>
  );
}
